import { Editor, Path, Transforms } from 'slate';
import { v4 as uuid4 } from 'uuid';

import { ReactEditor } from 'slate-react';
import React from 'react';
import { icons } from '@ublend-npm/aulaui-next';
import { isGenericLtiElement, genericLtiType } from './genericLtiType';
import { AulaPlugin, BlockMenuItemRendererProps } from '../plugins';
import { LtiBlock } from '../../components/LtiBlock/LtiBlock';
import { LtiLinkBlock } from '../../components/LtiBlock/LtiLinkBlock';
import { NestedMenuItem } from '../../components/NestedMenu/NestedMenu';
import { LtiProviderIcon } from '../../components/LtiProviderIcon/LtiProviderIcon';
import {
  AulaEditorContext,
  AulaEditorInstance,
  Lti1p3Provider,
  LtiProvider,
} from '../../types';
import { insertVoidElement } from '../../lib/commands';
import { Lti1p3Block } from '../../components/LtiBlockLink/Lti1p3Block';

export const insertGenericLti = (
  editor: AulaEditorInstance,
  path: Path,
  providerId: string,
  context?: Omit<AulaEditorContext, 'entities'>
) => {
  if (context == null) {
    throw new Error('context undefined');
  }

  const provider = context?.config?.ltiProviders?.find(
    (p: LtiProvider) => p.id === providerId
  );

  if (!provider) {
    throw new Error(`Provider '${providerId}' not found`);
  }

  const { createEntity } = context;
  const linkId = uuid4();
  const entity = createEntity({
    type: 'lti',
    data: { providerId },
    linkId,
    ...(provider.ltiVersion === '1.3' && provider.isDeepLinking
      ? { params: { launchType: 'deep-linking' } }
      : {}),
  });

  // insert at the end of current block
  setTimeout(() => {
    const end = Editor.end(editor, path);
    Transforms.select(editor, end);

    insertVoidElement(editor, {
      type: genericLtiType,
      linkId,
      entity,
    });
  }, 0);
};

export const genericLti: AulaPlugin = (editableProps, editor, context) => {
  const { isVoid } = editor;

  editor.isVoid = (element) => {
    if (element.type === genericLtiType) return true;

    return isVoid(element);
  };

  const genericLtiProviders =
    context?.config?.ltiProviders?.filter(
      (provider: LtiProvider) => provider.createdBy
    ) || [];

  const blockMenuItems = genericLtiProviders.reduce(
    (accum, provider) => ({
      ...accum,
      [provider.description]: ({
        element,
        attributes,
        onClose,
      }: BlockMenuItemRendererProps) => (
        <NestedMenuItem
          leftIcon={
            <LtiProviderIcon
              iconUrl={provider.iconUrl}
              altText={provider.description}
            />
          }
          text={provider.description}
          key={provider.id}
          attributes={{
            ...attributes,
            onClick: () => {
              insertGenericLti(
                editor,
                ReactEditor.findPath(editor, element),
                provider.id,
                context
              );
              onClose?.();
            },
          }}
        />
      ),
    }),
    {}
  );

  return {
    ...editableProps,
    blockMenu: {
      ...editableProps.blockMenu,
      Integrations: {
        ...(editableProps.blockMenu || {}).Integrations,
        ...blockMenuItems,
      },
    },
    renderSubMenuItem: (props) =>
      props.text === 'Integrations' ? (
        <NestedMenuItem
          leftIcon={<icons.ExtensionIcon />}
          rightIcon={<icons.ArrowRight />}
          {...props}
        />
      ) : (
        editableProps?.renderSubMenuItem?.(props) || (
          <NestedMenuItem {...props} />
        )
      ),
    renderElement: (props) => {
      const { element, attributes, children } = props;

      if (isGenericLtiElement(element)) {
        let providerId = '';
        let providerTitle;
        let providerIconUrl;
        let providerLtiVersion;
        let providerDetails = null;
        let providerLaunchContainer;

        if (element.entity) {
          ({ providerId } = context?.entities[element.entity].data || {});

          if (providerId) {
            providerDetails =
              context?.config?.ltiProviders?.find(
                (provider: LtiProvider) => provider.id === providerId
              ) || {};
            ({
              description: providerTitle,
              iconUrl: providerIconUrl,
              ltiVersion: providerLtiVersion,
              launchContainer: providerLaunchContainer,
            } = providerDetails);
          }
        }

        if (providerLtiVersion === '1.3') {
          const {
            loginUrl,
            targetUrl,
            clientId,
            iss,
          } = providerDetails as Lti1p3Provider;
          return (
            <div {...attributes}>
              <Lti1p3Block
                title={providerTitle || ''}
                icon={providerIconUrl || ''}
                loginUrl={loginUrl || ''}
                targetUrl={targetUrl || ''}
                clientId={clientId || ''}
                iss={iss || ''}
                element={element}
                resourceLinkId={context?.entities[element.entity]?.linkId}
              />
              {children}
            </div>
          );
        }

        const ltiEndpoint = `lti/providers/${providerId}/requests`;
        if (providerLaunchContainer === 'window') {
          return (
            <div {...attributes}>
              <LtiLinkBlock
                title={providerTitle || ''}
                icon={providerIconUrl}
                element={element}
                ltiEndpoint={ltiEndpoint}
              />
              {children}
            </div>
          );
        }

        return (
          <div {...attributes}>
            <LtiBlock
              title={providerTitle || ''}
              icon={
                <LtiProviderIcon
                  iconUrl={providerIconUrl}
                  altText={providerTitle}
                />
              }
              element={element}
              ltiEndpoint={ltiEndpoint}
            />
            {children}
          </div>
        );
      }

      return editableProps.renderElement?.(props) || <p />;
    },
  };
};
